import { apiService } from './api'
import { useTranslation } from 'react-i18next'

// WebSocket 베이스 URL — 운영은 fallback, 개발은 .env(VITE_WS_URL)로 주입
const WS_BASE = (import.meta as any).env.VITE_WS_URL || 'wss://api.kryzonic.com'

// Chat types
export interface ChatRoom {
  id: number
  user_id: number
  admin_id: number | null
  status: 'WAITING' | 'ACTIVE' | 'CLOSED'
  title: string
  user_name: string
  user_nickname?: string | null
  user_email: string
  admin_name: string | null
  assigned_at: string | null
  closed_at: string | null
  created_at: string
  updated_at: string
  unread_count: number
  last_message?: ChatMessage
  // 5분뒤 자동 종료 arm 상태 (관리자 UI 버튼 상태 동기화용)
  auto_close_armed_at?: string | null
  auto_close_armed_by_admin_id?: number | null
}

export interface ChatMessage {
  id: number
  chat_room_id: number
  sender_user_id: number | null
  sender_admin_id: number | null
  sender_name: string
  sender_type: 'user' | 'admin' | 'system'
  message_type: 'TEXT' | 'IMAGE' | 'SYSTEM'
  content: string | null
  image_filename?: string
  image_url?: string
  image_file_size?: number
  is_read: boolean
  read_at: string | null
  created_at: string
}

export interface ChatRoomListResponse {
  result: string
  message: string
  data: ChatRoom[]
  total: number
  page: number
  limit: number
}

export interface ChatMessageListResponse {
  result: string
  message: string
  data: ChatMessage[]
  total: number
  page: number
  limit: number
}

export interface ChatStats {
  total_rooms: number
  waiting_rooms: number
  active_rooms: number
  closed_rooms: number
  total_messages_today: number
  average_response_time_minutes: number
  websocket_connections: {
    total: number
    admin: number
    user: number
  }
}

class ChatService {
  // Get all chat rooms
  // ⚠️ apiService.get 은 이미 axios의 response.data(=API 응답 JSON)를 반환하므로
  //    그대로 돌려줘야 {data, total, page, limit} 가 보존됨.
  //    `response.data` 로 다시 풀면 rooms 배열만 나오고 total/page 가 사라져 페이징 깨짐.
  async getAllChatRooms(
    page: number = 1,
    limit: number = 20,
    status?: string,
    search?: string
  ): Promise<ChatRoomListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (status) params.append('status', status)
    if (search) params.append('search', search)

    const response = await apiService.get<ChatRoomListResponse>(`/api/v1/admin/chat/rooms?${params}`)
    return response as any
  }

  // Get waiting chat rooms
  async getWaitingChatRooms(): Promise<ChatRoomListResponse> {
    const response = await apiService.get<ChatRoomListResponse>('/api/v1/admin/chat/rooms/waiting')
    return response as any
  }

  // Get my assigned chat rooms
  async getMyChatRooms(
    page: number = 1,
    limit: number = 20,
    status?: string
  ): Promise<ChatRoomListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    if (status) params.append('status', status)

    const response = await apiService.get<ChatRoomListResponse>(`/api/v1/admin/chat/rooms/my?${params}`)
    return response as any
  }

  // Assign admin to chat room
  async assignChatRoom(roomId: number): Promise<void> {
    await apiService.post(`/api/v1/admin/chat/rooms/${roomId}/assign`, {})
  }

  // 관리자 메시지 수정 (2026-07-29)
  async updateMessage(roomId: number, messageId: number, content: string): Promise<void> {
    await apiService.put(`/api/v1/admin/chat/rooms/${roomId}/messages/${messageId}`, { content })
  }

  // 채팅방 제목 수정 (2026-07-29)
  async updateChatTitle(roomId: number, title: string): Promise<void> {
    await apiService.put(`/api/v1/admin/chat/rooms/${roomId}/title`, { title })
  }

  // 관리자 발신 채팅 시작 (2026-07-29) — created:false면 기존 진행 방(existing_room) 반환
  async initiateChat(userId: number, message: string, title?: string): Promise<{
    created: boolean
    room?: ChatRoom
    existing_room?: ChatRoom
  }> {
    const response = await apiService.post('/api/v1/admin/chat/rooms/initiate', {
      user_id: userId,
      message,
      ...(title && title.trim() ? { title: title.trim() } : {}),
    }) as any
    return (response?.data ?? response) as any
  }

  // Reassign closed chat room
  async reassignChatRoom(roomId: number): Promise<void> {
    await apiService.post(`/api/v1/admin/chat/rooms/${roomId}/reassign`, {})
  }

  // Get chat messages
  async getChatMessages(
    roomId: number,
    page: number = 1,
    limit: number = 50
  ): Promise<ChatMessageListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    })

    const response = await apiService.get<ChatMessageListResponse>(`/api/v1/admin/chat/rooms/${roomId}/messages?${params}`)
    return response as any
  }

  // Get all messages — 채팅 모달 진입 시 빠르게 표시할 최신 메시지 일괄 로드
  // ⚠️ 모든 페이지를 순회하면 긴 채팅방에서 로딩이 매우 느려짐.
  //    한 번에 큰 limit(=200)으로 잘라서 fetch — 그 이상 필요한 경우는 상위 컴포넌트에서
  //    "더 보기" UX를 추가하는 게 맞음.
  async getAllMessages(roomId: number): Promise<ChatMessage[]> {
    try {
      const response = await this.getChatMessages(roomId, 1, 200)

      let messageData: ChatMessage[] = []
      if (Array.isArray(response)) {
        messageData = response as any
      } else if (response && (response as any).data && Array.isArray((response as any).data)) {
        messageData = (response as any).data
      }

      // 백엔드는 created_at ASC 로 반환하지만 안전하게 한 번 더 정렬 (오래된 것부터)
      return [...messageData].sort((a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
    } catch (error) {
      console.error('Failed to load all messages:', error)
      throw error
    }
  }

  // Send chat message
  async sendMessage(roomId: number, content: string): Promise<void> {
    // This would be done through WebSocket in real implementation
    console.log('Sending message through WebSocket:', roomId, content)
  }

  // Upload image
  async uploadImage(roomId: number, file: File): Promise<void> {
    const formData = new FormData()
    formData.append('file', file)
    
    await apiService.post(`/api/v1/admin/chat/rooms/${roomId}/images`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  // Close chat room
  async closeChatRoom(roomId: number): Promise<void> {
    await apiService.delete(`/api/v1/admin/chat/rooms/${roomId}`)
  }

  // Delete a single chat message (soft delete) — 양쪽(유저/관리자) 화면에서 사라짐
  async deleteMessage(roomId: number, messageId: number): Promise<void> {
    await apiService.delete(`/api/v1/admin/chat/rooms/${roomId}/messages/${messageId}`)
  }

  // 채팅방의 사용자/게스트 메시지를 관리자가 읽음 처리 — 카톡 스타일 안읽음 배지 클리어
  async markRoomAsRead(roomId: number): Promise<void> {
    await apiService.post(`/api/v1/admin/chat/rooms/${roomId}/read`, {})
  }

  // Get chat statistics
  async getChatStats(): Promise<ChatStats> {
    const response = await apiService.get('/api/v1/admin/chat/stats')
    return (response.data?.data) || response.data || response as any
  }

  // WebSocket connection for real-time chat
  connectWebSocket(roomId: number, token: string): WebSocket {
    const wsUrl = `${WS_BASE}/api/v1/admin/chat/ws/${roomId}?token=${token}`
    return new WebSocket(wsUrl)
  }

  // 관리자 알림 WebSocket 연결 (전역)
  connectAdminNotificationWebSocket(token: string): WebSocket {
    const wsUrl = `${WS_BASE}/api/v1/admin/ws?token=${token}`
    return new WebSocket(wsUrl)
  }
}

export const chatService = new ChatService()
export default chatService